import React from "react";
import { Target, Link2, Utensils, ChefHat, ShoppingBasket } from "lucide-react";
import { SectionHeading, FieldLabel, Chip, StepInstructions } from "@/components/wizard/ui";
import { FieldInfo } from "@/components/wizard/InfoTip";
import Card from "@/components/wizard/StepCard";
import TrainingPeaksConnect from "@/components/TrainingPeaksConnect";
import { COOKING_STYLES } from "@/lib/planModel";
import { Input } from "@/components/ui/input";

// Step 2 · Setup — the "how you'll use it" questions, pulled out of Profile and
// Dietary so no single screen asks for everything at once (user feedback,
// June 2026: "there seems to be a lot of info in one go").
export default function Step2Setup({ plan, setPlan, patch }) {
  const profile = plan.profile || {};
  const dietary = plan.dietary || {};
  const wantsMealPlanning = plan.mealPlanning === true;

  const patchDiet = (updates) => setPlan((p) => ({
    ...p, dietary: { ...p.dietary, ...updates }, generated: null, selections: {},
  }));
  const setMealPlanning = (value) => setPlan((p) => ({
    ...p, mealPlanning: value, generated: null, selections: {},
  }));
  const setProfile = (updates) => setPlan((p) => ({ ...p, profile: { ...(p.profile || {}), ...updates } }));

  const usageLabel = profile.usage === "full" ? "Full plan"
    : profile.usage === "guidelines" ? "Guidelines only"
    : profile.usage === "tracker_only" ? "Tracker only" : null;

  return (
    <div data-testid="step2-setup">
      <SectionHeading eyebrow="Step 02" title="Setup"
        desc="Four quick choices that decide what the app builds for you — and which steps you'll even see." />

      <StepInstructions>
        Tell us how you'll use SPC, whether you want meal plans, how you like to eat and cook, and whether to link TrainingPeaks. Change any of it later from this step.
      </StepInstructions>

      <div className="grid grid-cols-1 gap-5 max-w-4xl min-w-0">

        {/* 01 · Usage — gates the whole wizard sequence. */}
        {profile.usage == null ? (
          <Card index="01" title="How will you use SPC?" Icon={Target} accent="#E2E30A" collapsible defaultOpen>
            <p className="text-[11px] text-zinc-300 mb-3 leading-relaxed">
              Tell us once and we'll only show the steps you need. You can change it any time.
            </p>
            <div className="grid gap-2">
              <button data-testid="usage-full" onClick={() => { setProfile({ usage: "full" }); setMealPlanning(true); }}
                className="text-left border border-white/15 hover:border-[#E2E30A] hover:bg-[#E2E30A]/[0.04] px-4 py-3 transition-colors">
                <p className="text-sm text-white font-bold">Full plan</p>
                <p className="text-[11px] text-zinc-300 mt-1">Personalised weekly meals, pathways, shopping list. Needs your training week.</p>
              </button>
              <button data-testid="usage-guidelines" onClick={() => { setProfile({ usage: "guidelines" }); setMealPlanning(false); }}
                className="text-left border border-white/15 hover:border-[#4C9AFF] hover:bg-[#4C9AFF]/[0.04] px-4 py-3 transition-colors">
                <p className="text-sm text-white font-bold">Guidelines only</p>
                <p className="text-[11px] text-zinc-300 mt-1">Daily macro targets from your training — no meal plans or shopping list.</p>
              </button>
              <button data-testid="usage-tracker-only" onClick={() => { setProfile({ usage: "tracker_only", useTP: profile.useTP ?? false }); setMealPlanning(false); }}
                className="text-left border border-white/15 hover:border-[#FF9500] hover:bg-[#FF9500]/[0.04] px-4 py-3 transition-colors">
                <p className="text-sm text-white font-bold">Tracker only</p>
                <p className="text-[11px] text-zinc-300 mt-1">Just the food tracker with a macro target. Skips Training, Plan, Pathways and Shopping.</p>
              </button>
            </div>
          </Card>
        ) : (
          <button data-testid="usage-change" type="button" onClick={() => setProfile({ usage: null })}
            className="w-full text-left border border-white/10 hover:border-[#E2E30A]/40 bg-[#141414] hover:bg-[#181818] px-4 py-3 transition-colors flex items-center justify-between gap-2">
            <span className="min-w-0">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">01 · Using SPC for</span>
              <span className="block text-sm text-zinc-100 mt-0.5 font-semibold">
                {usageLabel}<span className="text-[#E2E30A] font-normal"> — tap to change</span>
              </span>
            </span>
            <Target className="h-4 w-4 text-zinc-500 shrink-0" />
          </button>
        )}

        {/* 02 · Meal plans yes/no — decides Pathways + Shopping steps. */}
        <Card index="02" title="Do you want meal plans & a shopping list?" Icon={Utensils} accent="#E2E30A"
          collapsible defaultOpen={plan.mealPlanning == null}
          hint={plan.mealPlanning == null ? "Not chosen" : wantsMealPlanning ? "Yes" : "Targets only"}>
          <p className="text-[11px] text-zinc-300 mb-3 leading-relaxed">
            <b>Yes</b> gives you per-day meal pathways, recipes and a rounded shopping list. <b>No</b> keeps
            your daily numbers, Tracker and insights and skips the Pathways + Shopping steps.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="meal-planning-toggle-card">
            <Chip testId="meal-planning-yes" active={wantsMealPlanning}
              onClick={() => setMealPlanning(true)}>Yes — build my meals</Chip>
            <Chip testId="meal-planning-no" active={plan.mealPlanning === false}
              onClick={() => setMealPlanning(false)}>No — targets only</Chip>
          </div>
          {plan.mealPlanning == null && (
            <p className="text-[11px] text-zinc-300 mt-3" data-testid="meal-planning-undecided-note">
              Pick one to continue.
            </p>
          )}
        </Card>

        {/* 03 · Meal + cooking style — only relevant with meal plans on. */}
        {wantsMealPlanning && (
          <Card index="03" title="How you eat & cook" Icon={ChefHat} accent="#FF9500" collapsible defaultOpen
            hint={[(dietary.mealStyle || "meals") === "meals" ? "3 main meals" : dietary.mealStyle === "grazing" ? "Smaller meals + snacks" : `IF ${dietary.ifVariant || "16:8"}`,
              (COOKING_STYLES.find((c) => c.value === (profile.cookingStyle || "flexible")) || {}).label].join(" · ")}>
            <div className="space-y-7">
              <div>
                <FieldLabel info={
                  <FieldInfo testId="info-meal-style" title="Meal style">
                    How your day's food is split.
                    <div><b className="text-white">3 main meals</b> — larger breakfast, lunch and dinner.</div>
                    <div><b className="text-white">Smaller meals + snacks</b> — 3 lighter meals plus 2 high-protein snacks (easier on the gut, great for grazers).</div>
                    <div><b className="text-white">Intermittent Fasting</b> — 12:12 (gentlest), 14:10 or 16:8 (classic).</div>
                  </FieldInfo>
                }>Meal style</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  <Chip testId="meal-style-meals" active={(dietary.mealStyle || "meals") === "meals"}
                    onClick={() => patchDiet({ mealStyle: "meals" })}>3 main meals</Chip>
                  <Chip testId="meal-style-grazing" active={dietary.mealStyle === "grazing"}
                    onClick={() => patchDiet({ mealStyle: "grazing" })}>Smaller meals + snacks</Chip>
                  <Chip testId="meal-style-if" active={dietary.mealStyle === "if"}
                    onClick={() => patchDiet({ mealStyle: "if", ifVariant: dietary.ifVariant || "16:8" })}>Intermittent Fasting</Chip>
                </div>
                {dietary.mealStyle === "grazing" && (
                  <p className="text-[11px] text-zinc-300 mt-2">3 lighter mains + 2 snacks — your main plates come down in size automatically.</p>
                )}
                {dietary.mealStyle === "if" && (
                  <div className="mt-3 border border-[#E2E30A]/30 bg-[#E2E30A]/[0.04] px-3 py-3" data-testid="if-variant-picker">
                    <p className="text-[10px] uppercase tracking-wider text-[#E2E30A] font-semibold mb-2">Pick your window</p>
                    <div className="flex flex-wrap gap-2">
                      <Chip testId="if-12-12" active={(dietary.ifVariant || "16:8") === "12:12"}
                        onClick={() => patchDiet({ ifVariant: "12:12" })}>12:12 · Easiest</Chip>
                      <Chip testId="if-14-10" active={dietary.ifVariant === "14:10"}
                        onClick={() => patchDiet({ ifVariant: "14:10" })}>14:10 · Moderate</Chip>
                      <Chip testId="if-16-8" active={(dietary.ifVariant || "16:8") === "16:8"}
                        onClick={() => patchDiet({ ifVariant: "16:8" })}>16:8 · Challenging</Chip>
                    </div>
                    <p className="text-[11px] text-zinc-200 mt-2 leading-snug">
                      {(dietary.ifVariant || "16:8") === "12:12" && "3 meals in a 12-hour window (e.g. 8am–8pm)."}
                      {dietary.ifVariant === "14:10" && "3 meals in a 10-hour window (e.g. 10am–8pm)."}
                      {(!dietary.ifVariant || dietary.ifVariant === "16:8") && "2 meals + 1 optional snack in an 8-hour window (e.g. 12–8pm)."}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <FieldLabel>How do you like to cook?</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {COOKING_STYLES.map((c) => (
                    <Chip key={c.value} testId={`profile-cooking-${c.value}`}
                      active={(profile.cookingStyle || "flexible") === c.value}
                      onClick={() => setProfile({ cookingStyle: c.value })}>{c.label}</Chip>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-300 mt-1.5">
                  {(COOKING_STYLES.find((c) => c.value === (profile.cookingStyle || "flexible")) || {}).hint}
                </p>
                {profile.cookingStyle === "family" && (
                  <div className="mt-3 max-w-[220px]">
                    <FieldLabel>People in your household</FieldLabel>
                    <Input data-testid="profile-household-size" type="number" min="1" max="12" inputMode="numeric"
                      className="h-11 rounded-none bg-[#141414] border-white/10"
                      value={profile.householdSize || ""} placeholder="e.g. 4"
                      onChange={(e) => setProfile({ householdSize: e.target.value ? Math.max(1, parseInt(e.target.value, 10) || 1) : "" })} />
                    <p className="text-[11px] text-zinc-300 mt-1">Used to scale your shopping list for the whole family.</p>
                  </div>
                )}
              </div>

              <div>
                <FieldLabel info={
                  <FieldInfo testId="info-shopping-region" title="Grocery region">
                    Controls how your shopping list rounds up to real retail packs — a UK loaf is 800 g, a US loaf is ~680 g.
                  </FieldInfo>
                }>Grocery region</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {["UK", "US", "EU"].map((v) => (
                    <Chip key={v} testId={`profile-region-${v}`} active={(profile.shoppingRegion || "UK") === v}
                      onClick={() => setProfile({ shoppingRegion: v })}>{v}</Chip>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-300 mt-1.5 flex items-center gap-1.5">
                  <ShoppingBasket className="h-3 w-3 text-zinc-500" /> UK bread → 800 g loaf · US → 24 oz · EU → 500 g.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* 04 · TrainingPeaks — asked once, then minimised. */}
        {profile.useTP == null ? (
          <Card index="04" title="Do you use TrainingPeaks?" Icon={Link2} accent="#FFB454" collapsible defaultOpen>
            <p className="text-[11px] text-zinc-300 mb-3 leading-relaxed">
              Linking TrainingPeaks auto-syncs your sessions into your Tracker &amp; journal. Tell us once and we'll remember.
            </p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="tp-optin-yes" onClick={() => setProfile({ useTP: true })}
                className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold bg-[#FFB454] hover:bg-[#e6a24c] text-black transition-colors">
                Yes — link it
              </button>
              <button data-testid="tp-optin-no" onClick={() => setProfile({ useTP: false })}
                className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold border border-white/20 text-zinc-200 hover:bg-white/5 transition-colors">
                No — I don't use it
              </button>
              <button data-testid="tp-optin-skip" onClick={() => setProfile({ useTP: false })}
                className="px-4 py-2 text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white transition-colors">
                Skip for now
              </button>
            </div>
          </Card>
        ) : profile.useTP === true ? (
          <Card index="04" title="Connect TrainingPeaks" Icon={Link2} accent="#FFB454" collapsible defaultOpen
            hint={plan.trainingPeaks?.connected ? "Connected" : "Not connected"}>
            <TrainingPeaksConnect />
            <p className="text-[11px] text-zinc-300 mt-2 leading-relaxed">
              Syncs your real sessions into your Tracker &amp; journal. Your Training step still drives your macro targets. <span className="text-[#FFB454]">Calendar Sync requires TrainingPeaks Premium.</span>
            </p>
            <button data-testid="tp-optin-reset" onClick={() => setProfile({ useTP: null })}
              className="mt-3 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-white underline decoration-dotted">
              Change my answer
            </button>
          </Card>
        ) : (
          <button data-testid="tp-optin-reopen" type="button" onClick={() => setProfile({ useTP: null })}
            className="w-full text-left border border-white/10 hover:border-[#FFB454]/40 bg-[#141414] hover:bg-[#181818] px-4 py-3 transition-colors flex items-center justify-between gap-2">
            <span className="min-w-0">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">04 · TrainingPeaks</span>
              <span className="block text-sm text-zinc-300 mt-0.5">Not linked — <span className="text-[#FFB454]">tap to change</span></span>
            </span>
            <Link2 className="h-4 w-4 text-zinc-500 shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
}
